import { demoEngine } from './engine';

function ndjsonResponse(rows: any[]): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream({
    start(controller) {
      for (const r of rows) controller.enqueue(encoder.encode(JSON.stringify(r) + '\n'));
      controller.close();
    },
  });
  return new Response(stream, {
    status: 200,
    headers: { 'Content-Type': 'application/x-ndjson' },
  });
}

function marketHistoryRows(marketId: number, start: number, end: number, interval: number) {
  const ms = demoEngine.getMarketState(marketId);
  const baseRate = ms?.midRate ?? 0.05;
  const rows: any[] = [];
  const step = Math.max(interval, 60_000);
  for (let t = start; t <= end; t += step) {
    const drift = (Math.random() - 0.5) * 0.003;
    const r = Math.max(0.0001, baseRate + drift);
    rows.push({
      open: r.toFixed(6),
      close: r.toFixed(6),
      high: (r * 1.01).toFixed(6),
      low: (r * 0.99).toFixed(6),
      volume: ((ms?.volume24Usd ?? 1_000_000) / 96).toFixed(2),
      totalValue: ((ms?.volume24Usd ?? 1_000_000) * 10).toFixed(2),
      lendDepth: Math.round(ms?.lendDepositsTokens ?? 0),
      borrowDepth: Math.round(ms?.borrowDepositsTokens ?? 0),
      date: t,
    });
  }
  return rows;
}

let originalFetch: typeof fetch | null = null;

export function installFetchPatch() {
  if (originalFetch) return;
  originalFetch = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = typeof input === 'string' ? input : (input as Request).url ?? String(input);
    let parsed: URL;
    try {
      parsed = new URL(rawUrl, window.location.origin);
    } catch {
      return originalFetch!(input, init);
    }
    // Only intercept same-origin requests. All external hosts (CoinGecko,
    // WalletConnect explorer, RPC endpoints, etc.) go to the real network.
    if (parsed.origin !== window.location.origin) {
      return originalFetch!(input, init);
    }
    const path = parsed.pathname;
    const query = Object.fromEntries(parsed.searchParams.entries());

    await demoEngine.boot();

    const historyMatch = path.match(/^\/api\/p\/(floating_rate|fixed_rate)\/market\/(\d+)\/history/);
    if (historyMatch) {
      const marketId = Number(historyMatch[2]);
      const start = Number(query.start);
      const end = Number(query.end);
      const interval = Number(query.interval) || 15 * 60 * 1000;
      return ndjsonResponse(marketHistoryRows(marketId, start, end, interval));
    }

    if (path.startsWith('/api/')) {
      return ndjsonResponse([]);
    }
    return originalFetch!(input, init);
  }) as typeof fetch;
}
